import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertQuestionMappings,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
    // useUserIdLogin: true,
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = 'all'
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 名單式調查參數
  const { sfdSamplerId, sfdPId } = useSurveyParams()

  // 防止閃閃
  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
              console.log('✅ 強制加上 mobile class 避免抖動')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 以下皆為 1-based 寫法，convertVisibleIf 會自動 -1 還原為 0-based

  // A1(q12) 婚姻狀況：1未婚 2已婚 3未婚同居 4分居 5離婚 6喪偶
  // 有配偶或同居伴侶（A6、B7、D7、D9 限定查填）
  const hasPartner =
    '({q12} = 1 and ({q13} = 2 or {q13} = 3)) or ' +
    '({q12} = 2) or ' +
    '({q12} = 3) or ' +
    '({q12} = 4) or ' +
    '({q12} = 5 and ({q17} = 2 or {q17} = 3)) or ' +
    '({q12} = 6 and ({q18} = 2 or {q18} = 3))'

  // A4-1/A4-2/A4-3 限「有子女且子女年齡含 0~未滿6歲」者（A4 註記：2-1至2-3皆未勾選跳答A5）
  const hasChildUnder6 = '({q21} = 2) and ({q24} anyof [1, 2, 3])'

  // B1(q50)=1 有工作 → 續答 B2~B6-1；(2-1)或答完(2-2)一律跳答 B7
  const isEmployed = '{q50} = 1'

  const visibleIfConditionsInit = convertVisibleIf({
    // ===== 基本資料 =====
    // Q2-1(q3)=(1)無/不識字 → 跳答 Q3-1，即跳過 Q2-2
    q4: '{q3} <> 1',
    // Q4-1(q8) 勾「(1)獨居」(none) → 跳答 Q4-3，即跳過 Q4-2
    q9: '{q8} notcontains ["none"]',

    // ===== A 部分：婚育狀況 =====
    // A1 六個分支子題
    q13: '{q12} = 1',
    q14: '{q12} = 2',
    q15: '{q12} = 3',
    q16: '{q12} = 4',
    q17: '{q12} = 5',
    q18: '{q12} = 6',
    // A2 未婚原因：已婚/分居跳答 A4、離婚/喪偶跳答 A3，皆不問
    q19: '{q12} = 1 or {q12} = 3',
    // A3 結婚再婚意願：限未婚、未婚同居、離婚、喪偶（題幹自述限制）
    q20: '{q12} = 1 or {q12} = 3 or {q12} = 5 or {q12} = 6',
    // A4 子女人數與年齡
    q22: '{q21} = 2',
    q23: '{q21} = 2',
    q24: '{q21} = 2',
    q25: '({q21} = 2) and ({q24} anyof [1])',
    q26: '({q21} = 2) and ({q24} anyof [2])',
    q27: '({q21} = 2) and ({q24} anyof [3])',
    q28: '({q21} = 2) and ({q24} anyof [4])',
    q29: '({q21} = 2) and ({q24} anyof [5])',
    q30: '({q21} = 2) and ({q24} anyof [6])',
    // A4-1 托育花費 / A4-2 照顧方式 / A4-3 育嬰留停
    q31: hasChildUnder6,
    q32: hasChildUnder6,
    q33: '(' + hasChildUnder6 + ') and ({q32} anyof [18])',
    q34: hasChildUnder6,
    // A5 理想子女數
    q36: '{q35} = 1',
    q37: '({q35} = 1) and ({q36} = 1)',
    q38: '({q35} = 1) and ({q36} = 1)',
    q39: '({q35} = 1) and ({q36} = 2)',
    q40: '{q35} = 2',
    q41: '{q35} = 2',
    // A6 配偶／伴侶的理想子女數【限有配偶(同居伴侶)者查填】
    q42: hasPartner,
    q43: '(' + hasPartner + ') and ({q42} = 1)',
    q44: '(' + hasPartner + ') and ({q42} = 1) and ({q43} = 1)',
    q45: '(' + hasPartner + ') and ({q42} = 1) and ({q43} = 1)',
    q46: '(' + hasPartner + ') and ({q42} = 1) and ({q43} = 2)',
    q47: '(' + hasPartner + ') and ({q42} = 2)',
    q48: '(' + hasPartner + ') and ({q42} = 2)',

    // ===== B 部分：就業與工作 =====
    // B1(q50)=2 沒有 → B1-2；(2-2)沒找工作 → 原因與最主要一項
    q51: '{q50} = 2',
    q52: '({q50} = 2) and ({q51} = 2)',
    q53: '({q50} = 2) and ({q51} = 2)',
    // B2~B6-1 限有工作者
    q54: isEmployed,
    q55: isEmployed,
    q56: isEmployed,
    q57: isEmployed,
    q58: isEmployed,
    q59: isEmployed,
    q60: isEmployed,
    q61: isEmployed,
    // B7 配偶有無工作【限有配偶(同居伴侶)者查填】
    q62: hasPartner,
    // B9 六列：(2)沒有 → 跳下一列；A欄選「未曾恢復工作」→ 跳下一列
    // A欄「在離職__年__月後曾恢復工作」為 other 選項
    q65: '{q64} = 1',
    q66: '({q64} = 1) and ({q65} = "other")',
    q67: '({q64} = 1) and ({q65} = "other")',
    q69: '{q68} = 1',
    q70: '({q68} = 1) and ({q69} = "other")',
    q71: '({q68} = 1) and ({q69} = "other")',
    q73: '{q72} = 1',
    q74: '({q72} = 1) and ({q73} = "other")',
    q75: '({q72} = 1) and ({q73} = "other")',
    q77: '{q76} = 1',
    q78: '({q76} = 1) and ({q77} = "other")',
    q79: '({q76} = 1) and ({q77} = "other")',
    q81: '{q80} = 1',
    // 注意：B9-5 的 q82 是恢復類型、q83 是恢復原因（JSON 內順序與其他列相反）
    q82: '({q80} = 1) and ({q81} = "other")',
    q83: '({q80} = 1) and ({q81} = "other")',
    q85: '{q84} = 1',
    q86: '({q84} = 1) and ({q85} = "other")',
    q87: '({q84} = 1) and ({q85} = "other")',

    // ===== C 部分：經濟安全 =====
    // C1(q88) 選(1)沒有收入 → 跳轉至 C3(q92)，即跳過 C1-2、C2、C2-2
    q89: '{q88} = 2',
    q90: '{q88} <> 1',
    q91: '({q88} <> 1) and ({q90} = 2)',
    q93: '{q92} = 2',
    q95: '{q94} anyof [12]',
    q97: '{q96} = 2',

    // ===== D 部分：家庭照顧 =====
    // D3 各列：A欄「有」為 other 選項；B欄選(2)我是主要照顧者才問輪替照顧者
    q101: '{q100} = "other"',
    q102: '({q100} = "other") and ({q101} = 2)',
    q103: '({q100} = "other") and ({q101} = 2) and ({q102} = 1)',
    q105: '{q104} = "other"',
    q106: '({q104} = "other") and ({q105} = 2)',
    q107: '({q104} = "other") and ({q105} = 2) and ({q106} = 1)',
    q109: '{q108} = "other"',
    q110: '({q108} = "other") and ({q109} = 2)',
    q111: '({q108} = "other") and ({q109} = 2) and ({q110} = 1)',
    q113: '{q112} = "other"',
    q114: '({q112} = "other") and ({q113} = 2)',
    q115: '({q112} = "other") and ({q113} = 2) and ({q114} = 1)',
    // D7 配偶無酬照顧時間【限有配偶或同居伴侶者】
    q121: hasPartner,
    q122: hasPartner,
    q123: hasPartner,
    q124: hasPartner,
    q125: hasPartner,
    q126: hasPartner,
    // D9 提升配偶參與家務意願【限有配偶(同居伴侶)者查填】
    q128: hasPartner,
    q129: '(' + hasPartner + ') and ({q128} anyof [7])',

    // ===== E 部分：社會參與 =====
    // E1(q130) 勾「(8)都沒有」(none) 才續問 E2，其餘跳答 F1
    q131: '{q130} anyof ["none"]',

    // ===== F 部分：醫療健康與求助 =====
    q135: '{q134} = 2',
    q136: '({q134} = 2) and ({q135} anyof [9])',
    // F7(q140) 選「(1)沒有達到規律運動」(other) 才問 F8，選「(2)有」跳答 G1
    q141: '{q140} = "other"',

    // ===== H 部分：其他（排序題的「其他，請說明」）=====
    q147: '{q146} anyof [16]',
    q149: '{q148} anyof [11]',
    q151: '{q150} anyof [9]',
  })

  const notRequired = null
  const mappedRateChoice = null
  const pageBreakQuestions = null

  const triggersConditionsInit = null
  // 選項繼承：複選的原因題 → 「最主要一項」單選題，只帶出前題勾選的選項
  const questionMappings = convertQuestionMappings({
    q40: 'q41', // A5-2 不想有小孩原因 → A5-2-a 最主要一項
    q47: 'q48', // A6-2 配偶不想有小孩原因 → A6-2-a 最主要一項
    q52: 'q53', // B1-2-2 沒找工作原因 → B1-2-2-a 最主要一項
  })

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  // 結尾語：本案客製（不使用 EZChoice 預設結語）
  const endSurveyMsg = (survey) => {
    setCompletionMessage(`<div>問卷到此結束，感謝您的協助。</div>`)
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving:
      requiresLogin.login || webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
